import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { useIsMobile } from '@/hooks/use-mobile';
import { SUBSCRIPTION_TIERS, getAllTiers, type SubscriptionTier, type BillingCycle } from '@/lib/subscription';

type PlanFeature = {
  id: string;
  text: string;
};

type PlanDisplayData = {
  tier: SubscriptionTier;
  description: string;
  featuresPreamble?: string;
  features: PlanFeature[];
  featured?: boolean;
};

const PLAN_DISPLAY: PlanDisplayData[] = [
  {
    tier: 'free',
    description: 'Run your first campaigns with clear, organized feedback',
    features: [
      { id: 'free-projects', text: '2 active projects' },
      { id: 'free-reviewers', text: 'Unlimited reviewers' },
      { id: 'free-variation', text: '1 variation per project' },
      { id: 'free-comments', text: 'Inline comments + annotations' },
      { id: 'free-link', text: 'Shareable link (no login required)' },
      { id: 'free-forwarding', text: 'Email forwarding → generate review' },
      { id: 'free-project-view', text: 'Project view (see all feedback in one place)' },
    ],
  },
  {
    tier: 'starter',
    description: 'Everything you need to run your email reviews',
    featuresPreamble: 'Everything in Free, plus:',
    features: [
      { id: 'starter-projects', text: '5 active projects' },
      { id: 'starter-variations', text: 'Up to 5 email variations per project' },
      { id: 'starter-history', text: 'Version history' },
    ],
  },
  {
    tier: 'pro',
    description: 'For managing multiple campaigns at once',
    featuresPreamble: 'Everything in Starter, plus:',
    featured: true,
    features: [
      { id: 'pro-projects', text: '12 active projects' },
      { id: 'pro-variations', text: 'Up to 10 email variations per project' },
    ],
  },
  {
    tier: 'unlimited',
    description: 'For high-volume workflows',
    featuresPreamble: 'Everything in Pro, plus:',
    features: [
      { id: 'unlimited-projects', text: 'Unlimited projects' },
      { id: 'unlimited-variations', text: 'Unlimited email variations per project' },
    ],
  },
];

function renderPreamble(text: string) {
  const keywords = ['Free', 'Starter', 'Pro'];
  for (const kw of keywords) {
    if (text.includes(kw)) {
      const parts = text.split(kw);
      return (
        <span>
          {parts[0]}<strong className="font-bold text-slate-800">{kw}</strong>{parts[1]}
        </span>
      );
    }
  }
  return text;
}

interface PlanCardsProps {
  billing: BillingCycle;
  currentTier: SubscriptionTier;
  onSelectPlan: (tier: SubscriptionTier) => void;
  getButtonText: (tier: SubscriptionTier) => string;
  isCurrentPlan: (tier: SubscriptionTier) => boolean;
  subscribed: boolean;
}

export const PlanCards = ({ billing, currentTier, onSelectPlan, getButtonText, isCurrentPlan, subscribed }: PlanCardsProps) => {
  const isMobile = useIsMobile();

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 py-4 md:py-8 gap-4 w-full max-w-7xl mx-auto px-6 items-stretch">
      {PLAN_DISPLAY.map((plan, idx) => {
        const tierConfig = SUBSCRIPTION_TIERS[plan.tier];
        const isCurrent = isCurrentPlan(plan.tier);
        const displayPrice = plan.tier === 'free' ? 0 : billing === 'monthly' ? tierConfig.monthlyPrice : tierConfig.annualMonthly;
        const buttonText = getButtonText(plan.tier);
        const isBillingChange = isCurrent && buttonText === 'Change Billing Period';
        const isDisabled = false;

        return (
          <motion.div
            key={plan.tier}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: idx * 0.1 }}
            whileHover={!isMobile && plan.tier !== 'free' ? { y: -6, boxShadow: '0 20px 40px -12px rgba(0,0,0,0.12)' } : undefined}
            className={cn(
              'relative flex flex-col justify-between min-w-[260px] flex-1 rounded-2xl p-6 lg:p-8 border transition-shadow duration-300',
              isCurrent
                ? 'bg-gradient-to-b from-white to-slate-50 border-accent shadow-2xl shadow-accent/20'
                : 'bg-white border-slate-200'
            )}
          >
            {plan.featured && (currentTier === 'free' || currentTier === 'starter') && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center bg-accent text-accent-foreground text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full whitespace-nowrap z-10 font-sans">
                <span>Most Popular</span>
              </div>
            )}

            <div className="flex flex-col h-full space-y-5">
              <div>
                <p className={cn('text-base font-semibold mb-1 font-sans', isCurrent ? 'text-slate-600' : 'text-slate-500')}>
                  {tierConfig.name}
                </p>
                <div className="flex items-baseline gap-1">
                  <div className="relative">
                    <AnimatePresence mode="popLayout">
                      <motion.p
                        key={plan.tier === 'free' ? 'free' : `${plan.tier}-${billing}`}
                        initial={{ y: 12, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: -12, opacity: 0 }}
                        transition={{ duration: 0.25, ease: 'easeInOut' }}
                        className="text-4xl font-bold tracking-tighter text-slate-900 font-display"
                      >
                        ${displayPrice}
                      </motion.p>
                    </AnimatePresence>
                  </div>
                  {displayPrice > 0 && (
                    <p className="text-base font-semibold text-slate-900 font-sans">per month</p>
                  )}
                </div>
                <p className="mt-2 text-sm text-slate-500 font-sans">
                  {plan.description}
                </p>
              </div>

              <div className={cn('h-px w-full', isCurrent ? 'bg-slate-200' : 'bg-slate-100')} />

              <div className="flex flex-col flex-grow space-y-3 pb-8">
                {plan.featuresPreamble && (
                  <p className="text-sm text-slate-500 mb-3 font-sans">
                    {renderPreamble(plan.featuresPreamble)}
                  </p>
                )}
                {plan.features.map(feature => (
                  <div key={feature.id} className="flex items-center gap-2.5 w-full">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center bg-accent/30">
                      <Check className="h-3 w-3 text-slate-700" strokeWidth={2.5} />
                    </div>
                    <span className="text-sm text-slate-600 font-sans">{feature.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <button
              disabled={isDisabled}
              onClick={() => onSelectPlan(plan.tier)}
              className={cn(
                "mt-auto w-full flex items-center justify-center rounded-full py-3 px-4 text-sm font-semibold transition-all duration-200 font-sans",
                isCurrent && !isBillingChange || plan.tier === 'free'
                  ? "border-2 border-slate-900 text-slate-900 bg-white"
                  : "bg-slate-900 text-white hover:bg-accent hover:text-accent-foreground"
              )}
            >
              {buttonText}
            </button>
          </motion.div>
        );
      })}
    </div>
  );
};
